import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export function useQueryParams<T extends Record<string, any>>(keyParam: T) {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const parsedSearch = Object.fromEntries(searchParams.entries());
    const paramNames = Object.keys(keyParam);
    const values: T = { ...keyParam };
    // eslint-disable-next-line fp/no-loops
    for (let i = 0; i < paramNames.length; ++i) {
      const paramName = paramNames[i] as keyof T;
      // eslint-disable-next-line immutable/no-mutation
      values[paramName] = (parsedSearch[paramNames[i]] ??
        values[paramName]) as T[keyof T];
    }
    return values;
  }, [keyParam, location.search]);

  const setQueryParam = useCallback(
    (key: keyof T, newValue: any, action: 'push' | 'replace' = 'push') => {
      const searchParams = new URLSearchParams(location.search);
      if (newValue === null || newValue === undefined) {
        searchParams.delete(key as string);
      } else {
        searchParams.set(key as string, String(newValue));
      }
      const searchString = searchParams.toString();
      history[action]({ search: searchString ? `?${searchString}` : '' });
    },
    [location.search, history],
  );

  return { queryParams, setQueryParam };
}
